// web/src/pages/jobs/NewJobWizard.tsx
//
// J-3 — 3-step "New job" wizard (Client & Basis → Items → Review & Create).
//
// Plan I scope: no BOM domain, items are hand-keyed via JobItemsGrid.
//
// Plan IV (Task 11): Migrated from PageContainer + a hand-rolled <ol> step
// breadcrumb + bespoke Back/Next/Create-Job rows to the canonical Wizard
// archetype. Each step body extracted as a local component. Shared form state
// stays in the parent and is threaded into each step as props. All three steps
// use a per-step `actions` override to preserve the existing gating behaviour
// (Step 2's Next gated on every line being valid) and the final step's
// primary action wording ("Create Job") plus its create-mutation pending
// state.
import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ClientPicker, type ClientPickerValue } from "@/components/client-picker";
import { JobItemsGrid, rowToRequest, type JobItemRow } from "@/components/job-items-grid";
import { Wizard, type WizardStep } from "@/archetypes/Wizard";
import { IntentButton } from "@/components/intent-button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { jobsApi, type CreateJobRequest } from "@/lib/jobs";
import { toast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

// ---------------------------------------------------------------------------
// Basis state shape
// ---------------------------------------------------------------------------

interface BasisState {
  clientId: number | null;
  branchId: number | null;
  ownerEmployeeId: number | null;
  expectedDispatchDate: string;
  customerReference: string;
  purchaseOrderNumber: string;
  description: string;
}

const EMPTY_BASIS: BasisState = {
  clientId: null,
  branchId: null,
  ownerEmployeeId: null,
  expectedDispatchDate: "",
  customerReference: "",
  purchaseOrderNumber: "",
  description: "",
};

// ---------------------------------------------------------------------------
// Step body components — shared form state injected as props
// ---------------------------------------------------------------------------

interface Step1Props {
  client: ClientPickerValue | null;
  setClient: (v: ClientPickerValue | null) => void;
  basis: BasisState;
  patchBasis: (patch: Partial<BasisState>) => void;
}

function Step1ClientBasis({ client, setClient, basis, patchBasis }: Step1Props) {
  return (
    <section aria-label="Step 1: Client & Basis" className="space-y-4">
      <div className="space-y-1.5">
        <Label className="text-xs font-semibold text-muted-foreground">Client (optional)</Label>
        <ClientPicker value={client} onChange={setClient} />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="job-expected-dispatch" className="text-xs font-semibold text-muted-foreground">Expected dispatch date</Label>
        <Input
          id="job-expected-dispatch"
          type="date"
          value={basis.expectedDispatchDate}
          onChange={(e) => patchBasis({ expectedDispatchDate: e.target.value })}
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="job-customer-ref" className="text-xs font-semibold text-muted-foreground">Customer reference (optional)</Label>
        <Input
          id="job-customer-ref"
          value={basis.customerReference}
          onChange={(e) => patchBasis({ customerReference: e.target.value })}
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="job-po" className="text-xs font-semibold text-muted-foreground">PO # (optional)</Label>
        <Input
          id="job-po"
          value={basis.purchaseOrderNumber}
          onChange={(e) => patchBasis({ purchaseOrderNumber: e.target.value })}
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="job-desc" className="text-xs font-semibold text-muted-foreground">Description (optional)</Label>
        <textarea
          id="job-desc"
          className="block w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 min-h-[100px]"
          rows={4}
          value={basis.description}
          onChange={(e) => patchBasis({ description: e.target.value })}
        />
      </div>
    </section>
  );
}

interface Step2Props {
  items: JobItemRow[];
  setItems: React.Dispatch<React.SetStateAction<JobItemRow[]>>;
}

function Step2Items({ items, setItems }: Step2Props) {
  return (
    <section aria-label="Step 2: Items" className="space-y-2">
      <p className="text-xs text-muted-foreground">
        Add line items by hand. Items are optional — you can add them later on the job detail page.
      </p>

      <JobItemsGrid items={items} onChange={setItems} editable />
    </section>
  );
}

interface Step3Props {
  client: ClientPickerValue | null;
  basis: BasisState;
  items: JobItemRow[];
  submitError: string | null;
}

const fmt = new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" });

function Step3Review({ client, basis, items, submitError }: Step3Props) {
  return (
    <section aria-label="Step 3: Review & Create" className="space-y-8 max-w-2xl mt-4">
      <div className="space-y-4">
        <h3 className="font-semibold text-lg text-foreground">Job Details</h3>
        <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2 text-sm">
          <dt className="text-muted-foreground">Client</dt>
          <dd className="font-medium text-foreground">{client?.fullName ?? "(none)"}</dd>
          
          <dt className="text-muted-foreground">Expected Dispatch</dt>
          <dd className="font-medium text-foreground">{basis.expectedDispatchDate || "—"}</dd>
          
          <dt className="text-muted-foreground">Customer Ref</dt>
          <dd className="font-medium text-foreground">{basis.customerReference || "—"}</dd>
          
          <dt className="text-muted-foreground">PO #</dt>
          <dd className="font-medium text-foreground">{basis.purchaseOrderNumber || "—"}</dd>
          
          <dt className="text-muted-foreground">Description</dt>
          <dd className="font-medium text-foreground">{basis.description || "—"}</dd>
        </dl>
      </div>

      <div className="space-y-4">
        <h3 className="font-semibold text-lg text-foreground">Items ({items.length})</h3>
        {items.length > 0 ? (
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="p-2 font-semibold text-left">Description</th>
                  <th className="p-2 font-semibold text-center w-24">Qty</th>
                  <th className="p-2 font-semibold text-right w-32">Unit ex GST</th>
                  <th className="p-2 font-semibold text-right w-24">GST rate</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {items.map((r) => (
                  <tr key={r._key} className="hover:bg-muted/20">
                    <td className="p-2">{r.description || <em className="text-muted-foreground">(blank)</em>}</td>
                    <td className="p-2 text-center">{r.quantity}</td>
                    <td className="p-2 text-right">{fmt.format(Number(r.unitPriceExGst) || 0)}</td>
                    <td className="p-2 text-right">{r.gstRate}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground bg-muted/20">
            No items added. They can be added later on the job detail page.
          </div>
        )}
      </div>

      {submitError && (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive" role="alert">
          {submitError}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// NewJobWizard
// ---------------------------------------------------------------------------

export interface NewJobWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function NewJobWizard({ open, onOpenChange }: NewJobWizardProps) {
  const navigate = useNavigate();
  const qc = useQueryClient();

  // Step 1 state
  const [client, setClient] = useState<ClientPickerValue | null>(null);
  const [basis, setBasis] = useState<BasisState>(EMPTY_BASIS);

  // Step 2 state
  const [items, setItems] = useState<JobItemRow[]>([]);

  // Step 3 state
  const [submitError, setSubmitError] = useState<string | null>(null);

  // ---------------------------------------------------------------------------
  // Validation
  // ---------------------------------------------------------------------------

  const itemsValid = useMemo(
    () => items.length === 0 || items.every((r) => rowToRequest(r) !== undefined),
    [items],
  );

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  function patchBasis(patch: Partial<BasisState>) {
    setBasis((b) => ({ ...b, ...patch }));
  }

  function buildPayload(): CreateJobRequest {
    return {
      clientId: client?.clientId ?? null,
      branchId: basis.branchId,
      ownerEmployeeId: basis.ownerEmployeeId,
      expectedDispatchDate: basis.expectedDispatchDate || null,
      customerReference: basis.customerReference.trim() || null,
      purchaseOrderNumber: basis.purchaseOrderNumber.trim() || null,
      description: basis.description.trim() || null,
      items: items.length > 0
        ? items.map((r, i) => ({ ...rowToRequest(r)!, sortOrder: i }))
        : null,
    };
  }

  // ---------------------------------------------------------------------------
  // Mutation
  // ---------------------------------------------------------------------------

  const createMut = useMutation({
    mutationFn: (body: CreateJobRequest) => jobsApi.create(body),
    onSuccess: (job) => {
      void qc.invalidateQueries({ queryKey: ["jobs"] });
      toast.success(`Job ${job.jobReferenceNo} created.`);
      navigate(`/jobs/${job.jobId}`);
    },
    onError: (e: Error) => setSubmitError(e.message),
  });

  function onSubmit() {
    setSubmitError(null);
    createMut.mutate(buildPayload());
  }

  // ---------------------------------------------------------------------------
  // Wizard steps
  // ---------------------------------------------------------------------------

  const steps: WizardStep[] = [
    {
      id: "client-basis",
      label: "Client & Basis",
      render: () => (
        <Step1ClientBasis
          client={client}
          setClient={setClient}
          basis={basis}
          patchBasis={patchBasis}
        />
      ),
      // Override: no Back on the first step.
      actions: (ctx) => (
        <>
          <div />
          <IntentButton onClick={ctx.goNext}>Next</IntentButton>
        </>
      ),
    },
    {
      id: "items",
      label: "Items",
      render: () => <Step2Items items={items} setItems={setItems} />,
      // Override: gate Next on every line being valid.
      actions: (ctx) => (
        <>
          <IntentButton intent="ghost" onClick={ctx.goPrev}>Back</IntentButton>
          <IntentButton disabled={!itemsValid} onClick={ctx.goNext}>Next</IntentButton>
        </>
      ),
    },
    {
      id: "review",
      label: "Review & Create",
      render: () => (
        <Step3Review
          client={client}
          basis={basis}
          items={items}
          submitError={submitError}
        />
      ),
      // Override: final step's primary action is "Create Job" (not the
      // archetype's default "Finish") and shows the create-mutation pending
      // state.
      actions: (ctx) => (
        <>
          <IntentButton intent="ghost" onClick={ctx.goPrev}>Back</IntentButton>
          <IntentButton onClick={onSubmit} loading={createMut.isPending}>Create Job</IntentButton>
        </>
      ),
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl h-[650px] flex flex-col" aria-describedby={undefined}>
        <DialogTitle className="sr-only">New Job</DialogTitle>
        <Wizard title="New Job" steps={steps} onFinish={onSubmit} mode="dialog" />
      </DialogContent>
    </Dialog>
  );
}
